import * as React from 'react'
import { Form, Input, Spin, Button, Tooltip } from 'antd'
import { debounce, set, cloneDeep } from 'lodash'
import produce from 'immer'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import InputNumber from 'elements/InputNumber'
import Select, { Option, getOptionProps } from 'elements/Select'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createDropdownRender,
  isReadOnly,
} from 'helpers/formViews'
import { allowPricing, createFilterUserSuppliers } from 'helpers/auth'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import { DEBOUNCE } from 'helpers/utils'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchSuppliers = debounce(this.searchSuppliers, DEBOUNCE)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.id].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                price: 0,
                database: 'Distributor',
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const suppliers = await this.props.getSuppliers({
        supplierIds: item.supplierId ? [item.supplierId] : undefined,
      })

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        suppliers: suppliers?.value?.data ?? {},
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'supplierId') {
          set(draft.item, 'supplierName', draft.suppliers.items.find((one) => one.id === value).displayName)
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  searchSuppliers = async (search) => {
    try {
      const { item } = this.state
      const response = await this.props.getSuppliers({
        search,
        supplierIds: item.supplierId ? [item.supplierId] : undefined,
      })

      this.setState({ suppliers: response.value.data })
    } catch (error) {
      showError({ error })
    }
  }

  render() {
    const { item, fieldSettings, suppliers } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Form.Item label={createLabel('supplierId')}>
            {createFieldDecorator('supplierId')(
              <Select
                onChange={(value) => this.setItemValue('supplierId', value)}
                dropdownRender={createDropdownRender(suppliers)}
                onSearch={this.searchSuppliers}
                allowClear={false}
                readOnly={readOnly}
                showSearch
              >
                {(suppliers?.items ?? []).filter(createFilterUserSuppliers(item.supplierId)).map((each) => (
                  <Option key={each.id} value={each.id}>
                    <span {...getOptionProps(each)}>{each.displayName}</span>
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label={createLabel('supplierItemNumber')}>
            {createFieldDecorator('supplierItemNumber')(<Input readOnly={readOnly} />)}
          </Form.Item>
          {allowPricing() && (
            <Form.Item label={createLabel('price')}>
              {createFieldDecorator('price')(<InputNumber readOnly={readOnly} min={0} currency />)}
            </Form.Item>
          )}
          <Form.Item label={createLabel('description')}>
            {createFieldDecorator('description')(<Input.TextArea rows={5} readOnly={readOnly} />)}
          </Form.Item>
        </div>
        <div className="form-buttons-container">
          <Button onClick={this.props.onCancel}>{t(readOnly ? 'close' : 'cancel')}</Button>{' '}
          {!readOnly && (
            <Tooltip title={t('childRecordSaveInfo')} placement="topLeft">
              <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                {!this.props.linkTargetRecord ? t('add') : t('update')}
              </Button>
            </Tooltip>
          )}
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
